import { SubmissionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { parseDateLikeToLocalDateTime } from "util/dates";

/** Submission-derived context and status flags shared across the Reviewer Preparation step. */
export function useReviewerPreparationContext() {
  const { tenantId = "" } = useParams();
  const { isStaff } = useCurrentUser();
  const { submission, canEdit, isFrozen, isRejected, requestDetailsEdit } = useWasteStepper();

  const status = useMemo(() => submission?.status ?? SubmissionStatus.InProgress, [submission]);
  const persistedReviewers = useMemo(() => submission?.reviewers ?? [], [submission]);
  const documents = useMemo(() => submission?.documents ?? [], [submission]);

  // Staff can re-send from their own initial review; it's the only way to
  // re-route after a config change at that status.
  const canSendForReview = useMemo(
    () =>
      status === SubmissionStatus.NotStarted ||
      status === SubmissionStatus.InProgress ||
      (isStaff && status === SubmissionStatus.EncampInitialReview),
    [status, isStaff]
  );
  const disabled = !canEdit;

  const submissionDueDate = useMemo(
    () => (submission?.dueDate ? parseDateLikeToLocalDateTime(submission.dueDate) : undefined),
    [submission?.dueDate]
  );

  return {
    tenantId,
    isStaff,
    submission,
    canEdit,
    isFrozen,
    status,
    persistedReviewers,
    documents,
    canSendForReview,
    isRejected,
    disabled,
    submissionDueDate,
    requestDetailsEdit,
  };
}
